<template>
	<v-form ref="createForm" class="w-100" style="max-width: 900px" @submit.prevent="onSubmit">
		<template v-for="(field, index) in props.fieldDefinitions" :key="index">
			<!-- Single-Relation (belongsTo) -->
			<div v-if="field.type === 'relation-single'" class="mb-4">
				<label :for="field.name">Parent</label>
				<v-autocomplete
					:items="field.options"
					:item-title="getOptionTitle"
					item-value="id"
					:label="field.label"
					:name="field.name"
					v-model="formData[field.name]"
					hide-selected
					hide-details />
			</div>

			<!-- Richtext -->
			<div v-else-if="field.type === 'richtext'" class="mb-4">
				<label :for="field.name">{{ field.label }}</label>
				<v-spacer class="mb-1"></v-spacer>
				<QuillEditor
					theme="snow"
					v-model:content="formData[field.name]"
					:options="quillOptions"
					contentType="html" />
			</div>

			<!-- Textarea -->
			<v-textarea
				v-else-if="field.type === 'textarea'"
				:label="field.label"
				:name="field.name"
				v-model="formData[field.name]" />

			<!-- Checkbox -->
			<v-checkbox
				v-else-if="field.type === 'checkbox'"
				class="mb-3"
				hide-details
				:label="field.label"
				:name="field.name"
				v-model="formData[field.name]" />

			<!-- Number -->
			<v-text-field
				v-else-if="field.type === 'number'"
				type="number"
				:label="field.label"
				:name="field.name"
				v-model="formData[field.name]" />

			<!-- Multi-Relation (belongsToMany) -->
			<div v-else-if="field.type === 'relation-multiple'" class="mb-4">
				<label :for="field.name">{{ field.label }}</label>
				<v-autocomplete
					multiple
					:items="field.options"
					:item-title="getOptionTitle"
					item-value="id"
					:label="field.label"
					:name="field.name"
					v-model="formData[field.name]"
					hide-selected
					hide-details />
			</div>

			<!-- Default (text/date/etc.) -->
			<v-text-field
				v-else
				:type="field.type"
				:label="field.label"
				:name="field.name"
				v-model="formData[field.name]" />
		</template>

		<v-btn color="primary" class="mt-4 w-100" size="large" @click="onSubmit">Save</v-btn>
		<div v-if="errorMessage" class="error-text mt-2">
			{{ errorMessage }}
		</div>
	</v-form>
</template>

<script setup>
	import { ref } from "vue"
	import { QuillEditor } from "@vueup/vue-quill"
	import axios from "axios"
	import "@vueup/vue-quill/dist/vue-quill.snow.css"

	// Simple helper to convert "docTypes" => "doc_types"
	function toSnakeCase(str) {
		return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)
	}

	const props = defineProps({
		fieldDefinitions: {
			type: Array,
			default: () => [],
		},
		adminModule: {
			type: Object,
			default: () => {},
		},
		editingModule: {
			type: Object,
			default: () => null,
		},
	})

	const formData = ref({})
	const errorMessage = ref("")

	const quillOptions = {
		theme: "snow",
		modules: {
			toolbar: [
				["bold", "italic", "underline", "strike"],
				[{ header: 1 }, { header: 2 }],
				[{ color: [] }, { background: [] }],
				[{ align: [] }],
				["clean"],
				["link", "image"],
				["blockquote", "code-block"],
				[{ list: "ordered" }, { list: "bullet" }],
				["blockquote", "code-block"],
			],
		},
	}

	// Initialize defaults
	props.fieldDefinitions.forEach((field) => {
		if (field.type === "relation-multiple") {
			formData.value[field.name] = []
		} else if (field.type === "checkbox") {
			formData.value[field.name] = false
		} else {
			// Covers 'relation-single', text, etc.
			formData.value[field.name] = ""
		}
	})

	// Populate formData if editing
	if (props.editingModule) {
		props.fieldDefinitions.forEach((field) => {
			// For checkboxes:
			if (field.type === "checkbox") {
				formData.value[field.name] = !!props.editingModule[field.name]
			}
			// For belongsToMany:
			else if (field.type === "relation-multiple") {
				let items = props.editingModule[field.name]
				if (!items) {
					// If the relationship method is docTypes => doc_types
					const snakeKey = toSnakeCase(field.name)
					items = props.editingModule[snakeKey]
				}
				formData.value[field.name] = items ? items.map((item) => item.id) : []
			}
			// For belongsTo single select or anything else:
			else {
				formData.value[field.name] = props.editingModule[field.name] ?? ""
			}
		})
	}

	const getOptionTitle = (option) => {
		return option.name ?? option.title ?? option.business_name ?? option.id ?? ""
	}

	async function onSubmit() {
		try {
			errorMessage.value = ""

			// Convert checkboxes to boolean
			props.fieldDefinitions.forEach((field) => {
				if (field.type === "checkbox") {
					formData.value[field.name] = !!formData.value[field.name]
				}
			})

			const base = `/admin/${props.adminModule.slug}/`
			const url = props.editingModule?.id ? base + props.editingModule.id + "/" : base + "create"

			const req = props.editingModule?.id ? axios.put : axios.post
			await req(url, formData.value)
			window.location.href = `/admin/${props.adminModule.slug}`
		} catch (err) {
			console.error(err)
			errorMessage.value = err.response?.data?.message || "Error creating record."
		}
	}
</script>

<style scoped>
	.error-text {
		color: red;
	}
</style>
